import { useEffect, useRef } from "react";

const INPUT_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

const isTypingTarget = (target) => {
  if (!target) return false;
  if (INPUT_TAGS.has(target.tagName)) return true;
  if (target.isContentEditable) return true;
  if (target.closest?.('[contenteditable="true"]')) return true;
  if (target.closest?.(".react-select__input-container input")) return true;
  return false;
};

const isElementVisible = (el) => {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0 && rect.width > 0;
};

const isHorizontallyScrollable = (el) => {
  if (!el) return false;
  if (el.scrollWidth <= el.clientWidth + 1) return false;
  const { overflowX } = window.getComputedStyle(el);
  return overflowX === "auto" || overflowX === "scroll" || overflowX === "overlay";
};

const getHorizontalScrollElement = (container) => {
  if (!container) return null;

  if (isHorizontallyScrollable(container)) return container;

  const rdtScroll = container.querySelector(
    ".rdt_responsiveWrapperScroll, .rdt_responsiveWrapperFixed"
  );
  if (isHorizontallyScrollable(rdtScroll)) return rdtScroll;

  for (const el of container.querySelectorAll("*")) {
    if (isHorizontallyScrollable(el)) return el;
  }

  return null;
};

const shouldHandleContainer = (container) => {
  if (!isElementVisible(container)) return false;

  const active = document.activeElement;
  if (container.contains(active)) return true;

  if (active === document.body || active === document.documentElement) {
    return true;
  }

  return container.matches(":hover");
};

/**
 * Custom hook to enable horizontal scrolling via Arrow keys (Left/Right)
 * @param {number} scrollAmount - Pixels to scroll per key press
 * @returns {React.RefObject} - Ref to be attached to the scroll container
 */
const useKeyScroll = (scrollAmount = 100) => {
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (isTypingTarget(e.target)) return;

      const container = scrollContainerRef.current;
      if (!container || !shouldHandleContainer(container)) return;

      const scrollEl = getHorizontalScrollElement(container);
      if (!scrollEl) return;

      const delta = e.key === "ArrowLeft" ? -scrollAmount : scrollAmount;
      const previousScrollLeft = scrollEl.scrollLeft;
      scrollEl.scrollLeft += delta;

      if (scrollEl.scrollLeft !== previousScrollLeft) {
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown, { passive: false });
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [scrollAmount]);

  return scrollContainerRef;
};

export default useKeyScroll;
